import React, { useEffect, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import _get from 'lodash/get';
import _keyBy from 'lodash/keyBy';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _omit from 'lodash/omit';
import _orderBy from 'lodash/orderBy';
import { Prompt, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useStatus, useData, isLoading, isNormal } from 'resift';
import usePrevious from '@sift/resift/usePrevious';
import getHostName from 'helpers/getHostName';
import getRichTextCharCount from 'pages/Homepage/components/WidgetEdit/widgetSettings/RichTextWidgetSettings/helpers/getRichTextCharCount';
import buildWidgetWithDefaultValues from 'pages/Homepage/helpers/buildWidgetWithDefaultValues';
import makeUpdateWidget from 'fetches/homepageWidgets/makeUpdateWidget';
import createWidgetLayout from 'fetches/homepageWidgets/createWidgetLayout';
import getWidgets from 'fetches/homepageWidgets/getWidgets';
import showSnackbar from 'helpers/showSnackbar';
import { Formik, Form } from 'formik';
import TextField from '@material-ui/core/TextField';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import LoadingButton from 'atoms/LoadingButton';
import Card from 'pages/Homepage/components/molecules/WidgetConfigCard';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import WidgetTypeCard from 'pages/Homepage/components/WidgetTypeCard';
import RichTextWidgetSettings from './widgetSettings/RichTextWidgetSettings';
import MyTeamWidgetSettings from './widgetSettings/MyTeamWidgetSettings';
import PersonListsByDateWidgetSettings from './widgetSettings/PersonsListByDateWidgetSettings';
import { Widget, WidgetUpdateRequest } from 'pages/Homepage/typings';
import {
  DEFAULT_RICH_TEXT_CONTENT,
  MAX_CHAR_COUNT_RICH_TEXT,
  MAX_CHAR_COUNT_NAME,
  RICH_TEXT_TYPE_ID,
  MY_TEAM_TYPE_ID,
  PERSONS_LISTS_BY_DATE_TYPE_ID,
  MAX_CHAR_COUNT_DESCRIPTION,
  WIDGET_NAME_FIELD_DESCRIPTION,
  PREVIEW_PARAM_NAME,
} from 'pages/Homepage/helpers/consts';
import { PersonListsByDatePersonList } from 'pages/Homepage/typings';

const WIDGET_SETTINGS = {
  [RICH_TEXT_TYPE_ID]: {
    nameFieldDescription: WIDGET_NAME_FIELD_DESCRIPTION,
    Component: RichTextWidgetSettings,
  },
  [MY_TEAM_TYPE_ID]: {
    nameFieldDescription: WIDGET_NAME_FIELD_DESCRIPTION,
    Component: MyTeamWidgetSettings,
  },
  [PERSONS_LISTS_BY_DATE_TYPE_ID]: {
    nameFieldDescription: WIDGET_NAME_FIELD_DESCRIPTION,
    Component: PersonListsByDateWidgetSettings,
  },
};

interface Props {
  onDone: () => void;
}

function WidgetEdit({ onDone }: Props) {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const widgetId = location.pathname.split('/').pop() as string;
  const widgets: Array<Widget> = useSelector(state => _get(state, ['homepageWidgets', 'widgets']));
  const [shouldReset, setShouldReset] = useState(false);
  const widgetNames = !widgets.length ? [] : widgets.map(w => w.name);
  const _widget = widgets.find(w => w.id === widgetId) as Widget;
  const widget = buildWidgetWithDefaultValues(_widget);

  const widgetTypeId = _get(widget, 'widgetTypeId');

  // (note - Pearl) Temporarily removing preview widget button
  // const prevPreviewLayoutData = usePrevious(previewLayoutData);
  // const previewLayoutData = useData(createWidgetLayout);
  // const previewLayoutCreateStatus = useStatus(createWidgetLayout);
  // const clientSubdomain = useSelector((state: any) => state.client.subdomain);
  // const hostName = getHostName();
  // useEffect(() => {
  //   if (isNormal(previewLayoutCreateStatus) && prevPreviewLayoutData !== previewLayoutData) {
  //     window.open(
  //       `https://${clientSubdomain}.${hostName}.com?${PREVIEW_PARAM_NAME}=${previewLayoutData.id}`,
  //       '_blank',
  //     );
  //   }
  // }, [prevPreviewLayoutData, previewLayoutData, previewLayoutCreateStatus]);

  if (!widget) {
    return null;
  }

  const updateWidget = makeUpdateWidget(widgetId);
  const updateWidgetStatus = useStatus(updateWidget);

  showSnackbar({
    status: updateWidgetStatus,
    message: 'Successfully updated widget.',
    variant: 'success',
    errorMessage: 'An error occurred while updating the widget.',
  });

  // @ts-ignore
  const widgetSettings = WIDGET_SETTINGS[widgetTypeId];
  const SettingsComponent = widgetSettings.Component;

  const handleCreateWidgetPreview = () => {
    dispatch(createWidgetLayout({ widgets: [{ id: widget.id }] }));
  };

  const handleValidate = (values: any) => {
    let errors: any = {};
    const name = _get(values, 'name');
    const richTextDefault = _get(values, ['settings', 'richTextDefault']);
    const richTextExpanded = _get(values, ['settings', 'richTextExpanded']);
    const personLists: Array<PersonListsByDatePersonList> = _get(values, [
      'settings',
      'personLists',
    ]);

    if (!name.length) {
      errors.name = 'Name is required';
    }

    if (widgetNames.includes(name) && name !== widget.name) {
      errors.name = 'This name already exists, please use a different name';
    }

    if (name.length > MAX_CHAR_COUNT_NAME) {
      errors.name = `Name cannot be more than ${MAX_CHAR_COUNT_NAME} characters`;
    }

    if (richTextDefault || richTextExpanded) {
      const richTextDefaultCharCount = getRichTextCharCount(richTextDefault);
      const richTextExpandedCharCount = getRichTextCharCount(richTextExpanded);

      if (!richTextDefaultCharCount) {
        errors.richTextDefault = 'Default content is required';
      }

      if (richTextDefaultCharCount > MAX_CHAR_COUNT_RICH_TEXT) {
        errors.richTextDefault = `Default content cannot be more than ${MAX_CHAR_COUNT_RICH_TEXT} characters`;
      }

      if (richTextExpandedCharCount > MAX_CHAR_COUNT_RICH_TEXT) {
        errors.richTextExpanded = `Expanded content cannot be more than ${MAX_CHAR_COUNT_RICH_TEXT} characters`;
      }
    }

    if (personLists) {
      personLists.map((list, index: number) => {
        const baseListKey = `personLists[${index}]`;

        if (list._onPresetSelectStep) {
          errors[`${baseListKey}._onPresetSelectStep`] = `List #${list._targetListIndex +
            1}: Please confirm list preset`;
        } else {
          if (personLists.length > 1) {
            if (!list.name) {
              errors[`${baseListKey}.name`] = 'List name is required';
            }
          }

          if (!list.fieldObjectKey) {
            errors[`${baseListKey}.fieldObjectKey`] = 'Source Date Attribute is required';
          }
        }
      });
    }
    return errors;
  };

  return (
    <Formik initialValues={widget} onSubmit={() => {}} validate={handleValidate}>
      {props => {
        const { values, setFieldValue, dirty, resetForm, errors } = props;
        const widgetName = _get(values, 'name', '');
        const description = values.description;
        const defaultRichTextValue = _get(values, ['settings', 'richTextDefault']);
        const expandedRichTextValue = _get(values, ['settings', 'richTextExpanded']);

        const handleUpdateWidget = async () => {
          const widgetUpdateRequest: WidgetUpdateRequest = {};

          if (widgetName !== widget.name) {
            widgetUpdateRequest.name = widgetName;
          }

          if (description && description !== widget.description) {
            widgetUpdateRequest.description = description;
          }

          if (!_isEqual(values.settings, widget.settings)) {
            if (widget.widgetTypeId === RICH_TEXT_TYPE_ID) {
              if (
                _get(values, ['settings', 'richTextDefault']) !==
                _get(widget, ['settings', 'richTextDefault'])
              ) {
                const richTextDefaultCharCount = getRichTextCharCount(defaultRichTextValue);

                if (richTextDefaultCharCount) {
                  widgetUpdateRequest.settings = {
                    ...widgetUpdateRequest.settings,
                    richTextDefault: JSON.stringify(defaultRichTextValue),
                  };
                } else {
                  widgetUpdateRequest.settings = {
                    ...widgetUpdateRequest.settings,
                    richTextDefault: null,
                  };
                }
              }

              if (
                _get(values, ['settings', 'richTextExpanded']) !==
                _get(widget, ['settings', 'richTextExpanded'])
              ) {
                const richTextExpandedCharCount = getRichTextCharCount(expandedRichTextValue);

                if (richTextExpandedCharCount) {
                  widgetUpdateRequest.settings = {
                    ...widgetUpdateRequest.settings,
                    richTextExpanded: JSON.stringify(expandedRichTextValue),
                  };
                } else {
                  widgetUpdateRequest.settings = {
                    ...widgetUpdateRequest.settings,
                    richTextExpanded: null,
                  };
                }
              }
            } else if (widget.widgetTypeId === PERSONS_LISTS_BY_DATE_TYPE_ID) {
              const personLists = (values.settings && values.settings.personLists) || [];
              const orderedPersonLists = _orderBy(personLists, '_targetListIndex');
              widgetUpdateRequest.settings = {
                ...values.settings,
                personLists: orderedPersonLists,
              };
            } else {
              widgetUpdateRequest.settings = values.settings;
            }
          }

          await dispatch(updateWidget(widgetUpdateRequest));
          await dispatch(getWidgets(true));
          resetForm();
          onDone();
        };

        const handleCancel = () => {
          resetForm();
          setShouldReset(true);
        };

        const handleResetShouldReset = () => {
          setShouldReset(false);
        };

        return (
          <Form className={classes.root}>
            <div className={classes.left}>
              <Card>
                <CardContent>
                  <div className={classes.row}>
                    <Typography variant="h5">Name</Typography>
                    <Typography variant="subtitle2">
                      {widgetSettings.nameFieldDescription}
                    </Typography>
                    <TextField
                      id="widget-name"
                      name="widget-name"
                      fullWidth
                      error={!widgetName}
                      value={widgetName}
                      onChange={e => setFieldValue('name', e.target.value)}
                      InputLabelProps={{
                        classes: {
                          root: classes.inputLabel,
                          focused: classes.cssFocused,
                        },
                      }}
                      inputProps={{
                        maxLength: MAX_CHAR_COUNT_NAME,
                        'aria-label': 'widget name',
                      }}
                      helperText={errors.name && errors.name}
                    />
                    <div className={classes.characterCount}>
                      {widgetName.length}/{MAX_CHAR_COUNT_NAME}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <div className={classes.row}>
                    <Typography variant="h5">Description (Optional)</Typography>
                    <Typography variant="subtitle2">
                      This description will appear near the title of the widget to provide
                      additional context.
                    </Typography>
                    <TextField
                      id="widget-description"
                      name="widget-description"
                      fullWidth
                      value={values.description || ''}
                      onChange={e => setFieldValue('description', e.target.value)}
                      InputLabelProps={{
                        classes: {
                          root: classes.inputLabel,
                          focused: classes.cssFocused,
                        },
                      }}
                      multiline
                      inputProps={{
                        maxLength: MAX_CHAR_COUNT_DESCRIPTION,
                        'aria-label': 'widget description',
                      }}
                    />

                    <div className={classes.characterCount}>
                      {_get(description, 'length', 0)}/{MAX_CHAR_COUNT_DESCRIPTION}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <SettingsComponent
                values={values}
                onSetFieldValue={setFieldValue}
                shouldReset={shouldReset}
                originalValue={widget}
                resetShouldReset={handleResetShouldReset}
                errors={errors}
              />
            </div>
            <div>
              <WidgetTypeCard itemName={widgetTypeId} />
              {/* (note - Pearl) Temporarily removing preview widget button */}
              {/* <LoadingButton
                onClick={handleCreateWidgetPreview}
                color="secondary"
                loading={isLoading(previewLayoutCreateStatus)}
                label="Preview Widget"
                disabled={!_isEmpty(errors)}
              /> */}
            </div>

            <PageFormToolbar
              dirty={dirty || !_isEmpty(errors)}
              preContent={<PageFormErrorMessage errors={errors} />}
            >
              <CancelButton onClick={handleCancel} />
              <SaveButton
                label="Save and Next"
                onClick={handleUpdateWidget}
                disabled={!dirty || !_isEmpty(errors)}
              />
            </PageFormToolbar>
            <Prompt
              when={dirty}
              message={
                'Your work is not saved, or you have errors that need to be addressed! Are you sure you want to leave?'
              }
            />
          </Form>
        );
      }}
    </Formik>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  row: {
    marginBottom: theme.spacing(3),
  },
  inputLabel: {
    '&$cssFocused': {
      color: theme.palette.secondary.main,
    },
  },
  cssFocused: {},
  characterCount: {
    height: 18,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  left: {
    flex: 1,
    marginRight: theme.spacing(2.25),
  },
}));

export default WidgetEdit;
